import {MessageGroupViewModel, SenderType} from '../viewmodel/MessageGroupViewModel';
import {MessageViewModelFactory} from '../viewmodel/MessageViewModelFactory';
import {ResponseDto} from '../dto/ResponseDto';
import {Injectable} from '@angular/core';
import {DialogViewModel} from '../viewmodel/DialogViewModel';
import {BehaviorSubject, Observable} from 'rxjs';
import {MessageViewModel} from '../viewmodel/MessageViewModel';
import {QuickReplyDto} from '../dto/QuickReplyDto';
import {ClientFileMessage} from './client-file.message';

@Injectable()
export class DialogViewModelCreatorService {
    private dialogViewModelSubject: BehaviorSubject<DialogViewModel>;
    private messageViewModels: MessageViewModel[] = [];
    private typingIndicatorFlag: boolean = false;
    private thinkingIndicatorFlag: boolean = false;
    private static TYPING_INDICATOR_GROUP_ID: number = -1;
    private static THINKING_INDICATOR_GROUP_ID: number = -2;
    private pendingDividerInsertion = false;

    constructor() {
        this.dialogViewModelSubject = new BehaviorSubject<DialogViewModel>(this.createDialogViewModel());
    }

    public showThinkingIndicator(): void {
        this.thinkingIndicatorFlag = true;

        this.emitDialogViewModel();
    }

    public showTypingIndicator(): void {
        this.thinkingIndicatorFlag = false;
        this.typingIndicatorFlag = true;

        this.emitDialogViewModel();
    }

    public addBotMessage(responseDto: ResponseDto): void {
        this.typingIndicatorFlag = false;
        this.thinkingIndicatorFlag = false;
        if (this.pendingDividerInsertion && !this.hasDivider()) {
            this.messageViewModels.push(MessageViewModelFactory.createNewMessagesDivider());
        }
        this.pendingDividerInsertion = false;
        this.messageViewModels.push(MessageViewModelFactory.createFromServerDto(responseDto));

        this.emitDialogViewModel();
    }

    public addUserTextMessage(text: string, attachmentName?: string): void {
        this.messageViewModels.push(MessageViewModelFactory.createFromClientText(text, undefined, attachmentName));

        this.emitDialogViewModel();
    }

    public addUserFileMessage(clientFielsMessage: ClientFileMessage): void {
        this.messageViewModels.push(MessageViewModelFactory.createFromClientFiles(clientFielsMessage));

        this.emitDialogViewModel();
    }

    public addHistory(history: (ResponseDto | string)[], lastReadAt?: string): boolean {
        this.clearDialog();
        // Reverse a copy: callers (e.g. getLastBotResultFromHistory) rely on the original array staying chronological.
        history = [...history].reverse();
        const lastReadAtDate = lastReadAt !== undefined && lastReadAt.length > 0 ? new Date(lastReadAt) : undefined;
        let dividerInserted = false;

        for (const historyEntry of history) {
            if (typeof historyEntry === 'string') {
                this.messageViewModels.unshift(MessageViewModelFactory.createFromClientText(historyEntry, false));
            } else if ((historyEntry as {sender?: string}).sender === 'client') {
                const clientMessage = historyEntry as unknown as {text: string; attachmentName?: string};
                this.messageViewModels.unshift(MessageViewModelFactory.createFromClientText(clientMessage.text, false, clientMessage.attachmentName));
            } else {
                this.messageViewModels.unshift(MessageViewModelFactory.createFromServerDto(historyEntry, false));
            }
        }

        if (lastReadAtDate !== undefined) {
            for (let i = 0; i < this.messageViewModels.length; i++) {
                const message = this.messageViewModels[i];
                if (message.sender === 'server' && message.date > lastReadAtDate && !dividerInserted) {
                    this.messageViewModels.splice(i, 0, MessageViewModelFactory.createNewMessagesDivider());
                    dividerInserted = true;
                    break;
                }
            }
        }

        this.pendingDividerInsertion = false;
        this.emitDialogViewModel();
        return dividerInserted;
    }

    public clearDialog(): void {
        this.messageViewModels = [];

        this.emitDialogViewModel();
    }

    public markDividerBeforeNextBotMessage(): void {
        this.pendingDividerInsertion = true;
    }

    public cancelPendingDivider(): void {
        this.pendingDividerInsertion = false;
    }

    public removeNewMessagesDivider(): void {
        this.messageViewModels = this.messageViewModels.filter(m => m.sender !== 'divider');
        this.pendingDividerInsertion = false;
        this.emitDialogViewModel();
    }

    private hasDivider(): boolean {
        return this.messageViewModels.some(m => m.sender === 'divider');
    }

    public getDialogViewModelObservable(): Observable<DialogViewModel> {
        return this.dialogViewModelSubject.asObservable();
    }

    private emitDialogViewModel(): void {
        const dialogViewModel = this.createDialogViewModel();
        this.dialogViewModelSubject.next(dialogViewModel);
    }

    private createDialogViewModel(): DialogViewModel {
        const messageGroups: MessageGroupViewModel[] = this.createMessageGroups();
        const quickReplies: QuickReplyDto[] | undefined = this.createQuickReplies();

        if (this.thinkingIndicatorFlag && !this.typingIndicatorFlag) {
            this.addThinkingIndicator(messageGroups);
        }
        if (this.typingIndicatorFlag && !this.thinkingIndicatorFlag) {
            this.addTypingIndicator(messageGroups);
        }
        return new DialogViewModel(messageGroups, quickReplies);
    }

    private addThinkingIndicator(messageGroups: MessageGroupViewModel[]): void {
        if (this.messageViewModels.length === 0) {
            messageGroups.push(new MessageGroupViewModel(DialogViewModelCreatorService.THINKING_INDICATOR_GROUP_ID, [MessageViewModelFactory.createThinkingIndicator()], 'server'));
        } else {
            const lastMessageGroup = messageGroups[messageGroups.length - 1];
            const sender = lastMessageGroup.sender;
            if (sender === 'client') {
                messageGroups.push(new MessageGroupViewModel(DialogViewModelCreatorService.THINKING_INDICATOR_GROUP_ID, [MessageViewModelFactory.createThinkingIndicator()], 'server'));
            } else if (sender === 'server') {
                lastMessageGroup.messages.push(MessageViewModelFactory.createThinkingIndicator());
            }
        }
    }

    private addTypingIndicator(messageGroups: MessageGroupViewModel[]): void {
        if (this.messageViewModels.length === 0) {
            messageGroups.push(new MessageGroupViewModel(DialogViewModelCreatorService.TYPING_INDICATOR_GROUP_ID, [MessageViewModelFactory.createTypingIndicator()], 'server'));
        } else {
            const lastMessageGroup = messageGroups[messageGroups.length - 1];
            const sender = lastMessageGroup.sender;
            if (sender === 'client') {
                messageGroups.push(new MessageGroupViewModel(DialogViewModelCreatorService.TYPING_INDICATOR_GROUP_ID, [MessageViewModelFactory.createTypingIndicator()], 'server'));
            } else if (sender === 'server') {
                lastMessageGroup.messages.push(MessageViewModelFactory.createTypingIndicator());
            }
        }
    }

    private createQuickReplies(): QuickReplyDto[] | undefined {
        if (this.messageViewModels.length > 0 && this.messageViewModels[this.messageViewModels.length - 1].sender === 'server' && !this.typingIndicatorFlag) {
            return this.messageViewModels[this.messageViewModels.length - 1].quickReplies;
        }
        return undefined;
    }

    private createMessageGroups(): MessageGroupViewModel[] {
        const messageGroups: MessageGroupViewModel[] = [];
        let messages: MessageViewModel[] = [];
        let activeGroupSender: SenderType | undefined;

        let groupId: number = 1;
        for (let i = 0; i < this.messageViewModels.length; i++) {
            const element = this.messageViewModels[i];
            const hasSenderChanged = element.sender !== activeGroupSender;
            if (hasSenderChanged) {
                activeGroupSender = element.sender;
                messages = [];
                const messageGroupViewModel = (element.sender === 'server') ? new MessageGroupViewModel(groupId, messages, activeGroupSender, element.botAvatarImageUrl) : new MessageGroupViewModel(groupId, messages, activeGroupSender);
                messageGroups.push(messageGroupViewModel);
                groupId++;
            }
            messages.push(element);
        }
        return messageGroups;
    }
}
